import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useModal } from 'context/ModalContext';
import { CAMPO_DA_ACAO, ConfirmacaoDaAcao, ResultadoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';

interface Props {
  callback: (obj: { id: string; message?: string }) => void;
  id: string;
  status: string | undefined;
  data?: any;
}

/**
 * Feedback da Ação ECOA (ECN-57): mesmo título, rótulo por status, limite de 20 a 200 caracteres e "Mandou bem!" do
 * modal antigo, na moldura da nova identidade.
 */
export default function ModalFeedback(props: Props) {
  const modalID = 'validation-feedback';

  const [message, setMessage] = useState<string>();

  const { openModal, closeModal } = useModal();
  const { callback, status, id, data } = props;

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [success, setSuccess] = useState(false);

  const text = () => {
    if (status === 'rejeitado') {
      return 'Conte à equipe o motivo da entrega ter sido rejeitada:';
    } else if (status === 'aprovado') {
      return 'Conte à equipe o motivo da entrega ter sido validada:';
    } else if (status === 'promovido') {
      return 'Conte à equipe o motivo da entrega ter sido promovida:';
    } else if (status === 'dobrado') {
      return 'Conte à equipe o motivo da entrega ter sido bonificada:';
    } else if (status === 'selo') {
      return 'Conte à equipe o motivo da entrega ter sido indicada para o selo:';
    }
  };

  function handleSubmit() {
    if (typeof message === 'string' && message.trim().length) {
      if (message.trim().length > 200 || message.trim().length < 20) {
        return toast(
          `Seu feedback não pode ter menos de 20 caracteres ou mais de 200 caracteres. Você digitou ${message.trim().length} ${
            message.trim().length > 1 ? `caracteres` : `caracter`
          }.`,
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            type: 'error',
          },
        );
      }

      callback({ id: 'feedback', message: message.trim() });
      setSuccess(true);
    }
  }

  useEffect(() => {
    if (typeof id === 'string' && typeof openModal === 'string' && openModal === `${modalID}-${id}`) {
      setMessage(undefined);
      setSuccess(false);
      setIsOpen(true);
    } else {
      setMessage(undefined);
      setSuccess(false);
      setIsOpen(false);
    }
  }, [id, openModal]);

  if (!modalIsOpen) return <></>;

  if (success) {
    return (
      <ResultadoDaAcao title="Mandou bem!" onClose={() => closeModal()}>
        O feedback contribui com o engajamento e desenvolvimento de sua equipe.
      </ResultadoDaAcao>
    );
  }

  return (
    <ConfirmacaoDaAcao
      title="Dê um feedback sobre essa Ação ECOA"
      subtitle={data?.title}
      size="md"
      onClose={() => closeModal()}
      onConfirm={() => {
        handleSubmit();
      }}
      confirmLabel="Enviar feedback"
      confirmDisabled={!message || !message.trim().length || message.trim().length < 20}
    >
      <div>
        {/* SPEC_DEVIATION: com a ação aguardando ou ciente o rótulo de hoje fica vazio; aqui o campo ganha um rótulo
            genérico para ter nome acessível (WCAG 4.1.2). Nos demais status o texto é o de hoje. */}
        <label htmlFor="feedback-ecoa-textarea" className="mb-1.5 block text-jj-label font-semibold text-content-soft">
          {text() ?? 'Conte à equipe o que achou dessa Ação ECOA:'}
        </label>
        <textarea
          id="feedback-ecoa-textarea"
          className={CAMPO_DA_ACAO}
          rows={4}
          maxLength={200}
          minLength={20}
          onChange={e => setMessage(e.target.value)}
          defaultValue={message}
          aria-describedby="feedback-ecoa-limites feedback-ecoa-char-count"
        ></textarea>
        {/* O mínimo explica por que "Enviar feedback" segue desabilitado (WCAG 3.3.2). */}
        <div className="mt-1.5 flex items-start justify-between gap-3 text-[10px] text-content-soft">
          <p id="feedback-ecoa-limites" className="m-0">
            Mínimo de 20 e máximo de 200 caracteres.
          </p>
          <p id="feedback-ecoa-char-count" className="m-0 shrink-0">
            {typeof message === 'string' ? message?.trim().length : 0}
            /200
          </p>
        </div>
      </div>
    </ConfirmacaoDaAcao>
  );
}
